import { signup, signOut, sendVerificationEmail, UserAccessError } from '@alchemiesolns/user-access';
import { saveUserProfile } from '@alchemiesolns/store-api';
import type { RegistrationFormData } from '@alchemiesolns/product-registration';

function userAccessErrorToFriendlyMessage(err: UserAccessError): string {
  switch (err.code) {
    case 'email-in-use':
      return 'An account with this email already exists.';
    case 'invalid-email':
      return 'Invalid email address.';
    case 'weak-password':
      return 'Password is too weak. Use at least 6 characters.';
    case 'operation-not-allowed':
      return 'Email/password sign-up is not enabled for this project.';
    default:
      return err.message || 'Account creation failed.';
  }
}

/**
 * Creates a Firebase Auth user (via @alchemiesolns/user-access), then POSTs
 * the user's profile to alchemie-commerce (via @alchemiesolns/store-api).
 * Signs out so the user signs in from a clean state afterward.
 *
 * App orchestrates the two calls explicitly — user-access does not
 * side-effect into store-api.
 */
export async function registerFirebaseAccount(formData: RegistrationFormData): Promise<void> {
  const password = formData.password ?? '';
  if (!password) {
    throw new Error('Password is required');
  }

  const name = formData.name.trim();
  const email = formData.email.trim();
  const institution = (formData.institution ?? '').trim();

  try {
    await signup(email, password);
  } catch (e) {
    if (e instanceof UserAccessError) {
      throw new Error(userAccessErrorToFriendlyMessage(e));
    }
    throw e;
  }

  try {
    await sendVerificationEmail();
  } catch (e) {
    await signOut().catch(() => undefined);
    throw e instanceof Error ? e : new Error('Account created, but verification email could not be sent.');
  }

  try {
    await saveUserProfile({
      name,
      institution: institution || null,
      marketingConsent: formData.marketingConsent === true,
    });
  } catch (e) {
    // Profile save failed after the Firebase user was created. Keep the
    // account for retry rather than deleting — store-api will surface a
    // clear error. (The prior implementation deleted on failure; that's
    // no longer safe because the user-access signup already triggered a
    // sign-in state. We leave cleanup to the user re-signing up or
    // support — matches Stripe's "best-effort" semantics.)
    await signOut().catch(() => undefined);
    throw e instanceof Error ? e : new Error('Registration failed');
  }

  await signOut();
}
